import React, { useRef } from 'react'
import { calculateRGB } from '../../utils/branding-utils'

// Colors referenced from https://duotones.co/
const placeholderColor = (
  ctx: CanvasRenderingContext2D,
  alt: string,
  options: { width?: number; height?: number; color?: string },
): CanvasGradient => {
  const value: number = alt
    .split('')
    .map((a) => a.charCodeAt(0))
    .reduce((a, b) => a + b, 0)
  const gradient = ctx.createLinearGradient(0, options.height || 100, options.width || 100, 0)
  const opacity = 0.5
  // Simulate opacity by blending with white
  function mixRGB(r: number, g: number, b: number, alpha = 1, blend = 255.0) {
    r = Math.round((1 - alpha) * blend + alpha * r)
    g = Math.round((1 - alpha) * blend + alpha * g)
    b = Math.round((1 - alpha) * blend + alpha * b)

    return `rgba(${r},${g},${b},1)`
  }
  if (options.color) {
    const { R, G, B } = calculateRGB(options.color)
    gradient.addColorStop(0, mixRGB(R, G, B, opacity / 2))
    gradient.addColorStop(1, mixRGB(R, G, B, opacity))
    return gradient
  }
  switch (value % 11) {
    case 0: // Spotify
      gradient.addColorStop(0, mixRGB(106, 255, 127, opacity))
      gradient.addColorStop(1, mixRGB(0, 0, 126, opacity))
      break
    case 1: // Blueberry
      gradient.addColorStop(0, mixRGB(1, 219, 254, opacity))
      gradient.addColorStop(1, mixRGB(127, 1, 211, opacity))
      break
    case 2: // Mountain Dew
      gradient.addColorStop(0, mixRGB(251, 240, 25, opacity))
      gradient.addColorStop(1, mixRGB(1, 171, 109, opacity))
      break
    case 3: // Yellow
      gradient.addColorStop(0, mixRGB(251, 205, 32, opacity))
      gradient.addColorStop(1, mixRGB(255, 93, 119, opacity))
      break
    case 4: // Pink Sunset
      gradient.addColorStop(0, mixRGB(220, 67, 121, opacity))
      gradient.addColorStop(1, mixRGB(17, 36, 94, opacity))
      break
    case 5: // Clouds
      gradient.addColorStop(0, mixRGB(255, 255, 255, opacity))
      gradient.addColorStop(1, mixRGB(145, 207, 248, opacity))
      break
    case 6: // Seafoam
      gradient.addColorStop(0, mixRGB(172, 212, 157, opacity))
      gradient.addColorStop(1, mixRGB(96, 36, 87, opacity))
      break
    case 7: // Electric Melon
      gradient.addColorStop(0, mixRGB(239, 0, 158, opacity))
      gradient.addColorStop(1, mixRGB(80, 98, 214, opacity))
      break
    case 8: // Purple Light
      gradient.addColorStop(0, mixRGB(222, 252, 254, opacity))
      gradient.addColorStop(1, mixRGB(134, 130, 217, opacity))
      break
    case 9: // Seashell
      gradient.addColorStop(0, mixRGB(253, 217, 226, opacity))
      gradient.addColorStop(1, mixRGB(101, 183, 214, opacity))
      break
    case 10: // Forrest
    default:
      gradient.addColorStop(0, mixRGB(1, 171, 109, opacity))
      gradient.addColorStop(1, mixRGB(36, 26, 95, opacity))
      break
  }
  return gradient
}

export const placeholderImage = (
  alt: string,
  options: { width?: number; height?: number; color?: string },
): string => {
  var canvas: HTMLCanvasElement = document.createElement('canvas')
  canvas.width = options.width || 100
  canvas.height = options.height || 100
  var ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.fillStyle = placeholderColor(ctx, alt, options)
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    if (alt) {
      ctx.fillStyle = '#000000'
      ctx.font = `bold ${
        canvas.height * 0.4
      }px 'Montserrat', 'Helvetica Neue', Arial, 'Noto Sans', sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(alt.charAt(0).toUpperCase(), canvas.width / 2, canvas.height / 2)
    }
  }
  return canvas.toDataURL()
}

interface IProps
  extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
  placeholder?: string
  placeholderColor?: string
}

export const Icon = (props: IProps): React.ReactElement => {
  const { src, alt, placeholderColor, ...imgProps } = props
  const ref = useRef<HTMLImageElement>(null)
  const placeholder =
    props?.placeholder ||
    placeholderImage((alt || '').trim(), {
      width: Number(imgProps.width),
      height: Number(imgProps.height),
      color: placeholderColor,
    }) ||
    '/imgs/placeholder-image.jpg'
  return (
    <img
      ref={ref}
      {...imgProps}
      src={src || placeholder}
      alt={alt}
      onError={function (e: React.SyntheticEvent<HTMLImageElement, Event>) {
        if (ref.current) {
          ref.current.onerror = null
          ref.current.src = placeholder
        }
      }}
    />
  )
}
